import { BindAll } from 'lodash-decorators'

import { observable, computed } from 'mobx'

import { formatName } from 'common/helpers/formatters'
import * as newrelic from 'common/helpers/new_relic'
import { VendorUserTiers } from 'common/server/server_types'
import { Flags } from 'common/server/user'
import UserBaseStore from 'common/stores/UserBaseStore'

import {
  show,
  ShowUserResponse,
  update,
  UserPersonalInformationEditOptions,
  UserPasswordEditOptions,
  UserNotificationFrequency,
} from 'vendor/server/user'

@BindAll()
export default class UserStore extends UserBaseStore {
  @observable.struct currentUser: ShowUserResponse
  @observable feature_flags: Flags

  @computed get currentVendor() {
    return this.currentUser?.vendor
  }

  @computed get numPremiumSeatsPurchased() {
    return this.currentVendor.billing.seats_available?.[VendorUserTiers.PREMIUM] || 0
  }

  async updatePersonalInformation(params: UserPersonalInformationEditOptions) {
    await update(params)
    this.showUser()
  }

  updatePassword(params: UserPasswordEditOptions) {
    return update(params)
  }

  async updateNotificationsSetting(params: UserNotificationFrequency) {
    await update(params)
    this.showUser()
  }

  async showUser() {
    const { data } = await show()
    this.currentUser = data
    this.feature_flags = data.feature_flags

    const featureFlagInitializePendo = this.feature_flags.pendo_analysis_tool.enabled

    this.maybeInitializePendo(
      {
        id: this.currentUser.id,
        email: this.currentUser.email,
        role: this.currentUser.role,
        type: 'VENDOR',
        account: {
          id: this.currentUser.vendor.id,
          name: formatName(this.currentUser.first_name, this.currentUser.last_name),
        },
      },
      featureFlagInitializePendo,
    )
    newrelic.setUserInfo({
      userId: this.currentUser.id,
      email: this.currentUser.email,
      vendorId: this.currentUser.vendor_id,
      vendorName: this.currentUser.vendor?.name,
      type: 'VENDOR',
    })
  }

  private hasPermission(permission: string) {
    return computed(() => this.currentUser?.permissions?.includes(permission)).get()
  }

  @computed get canUseWatchersByCustomer(): boolean {
    return this.hasPermission('can_use_watchers_by_customer')
  }
  @computed get canUseKanban(): boolean {
    return this.hasPermission('can_use_kanban')
  }
  @computed get canUseDashboard(): boolean {
    return this.hasPermission('can_use_dashboard')
  }
  @computed get canUseOrdersFilters(): boolean {
    return this.hasPermission('can_use_orders_filters')
  }
  @computed get canUseOrdersDownload(): boolean {
    return this.hasPermission('can_use_orders_download')
  }
  @computed get canUseBilling(): boolean {
    return this.hasPermission('can_use_billing')
  }
  @computed get canUseOrderMaterials(): boolean {
    return this.hasPermission('can_use_order_materials')
  }
  @computed get canUseAllPreviousOrders(): boolean {
    return this.hasPermission('can_use_all_previous_orders')
  }
  @computed get canManageSettings(): boolean {
    return this.hasPermission('can_manage_vendor_settings')
  }
  @computed get canManageUsers(): boolean {
    return this.hasPermission('can_manage_users')
  }
}
